import React, { useState, memo } from "react";
import { useTheme } from "@mui/material/styles";
import { Dialog, Grid } from "@mui/material";

import { Props, State } from "./interfaces";
import { DialogContent } from "./styles";
import DialogTitle from "../../../DialogTitle/DialogTitle";
import LoadingOverlay from "../../../LoadingOverlay/LoadingOverlay";
import DialogActions from "../../../DialogActions/DialogActions";
import { useTranslation } from "react-i18next";
import useAuthorization, { isAuthorizedEeGroup } from "../../../../custom-hooks/useAuthorization/useAuthorization";
import { AUTHORIZATIONS } from "../../../../utils/constants/authorization-constants";
import PendingApprovals from "./PendingApprovals";
import { useAppSelector } from "../../../../custom-hooks/redux/redux";
import { EmployeeGroupRef } from "../../../../models/employee-groups";

const PendingApprovalModal = ({ children, notifyParent, employee }: Props) => {
  const { t } = useTranslation();
  const tRP = "components.my_calendar.add_absence";
  const theme = useTheme();

  const { currentUserAuthorizations } = useAppSelector((state) => state.currentUserReducer);

  const hasAccess = isAuthorizedEeGroup(
    [AUTHORIZATIONS.EmployeesAccess.ApproveTimeAndLeaves],
    currentUserAuthorizations,
    employee?.groups?.map((group: EmployeeGroupRef) => group?.id) ?? []
  );

  const allowedToViewOnly = isAuthorizedEeGroup(
    [AUTHORIZATIONS.EmployeesAccess.ViewNonSalaryAndCompensationData],
    currentUserAuthorizations,
    employee?.groups?.map((group: EmployeeGroupRef) => group?.id) ?? []
  );

  const [state, setState] = useState<State>({
    openDialog: false,
    loading: false,
    error: "",
    items: [],
    isMutated: false,
    mutatingStatus: "",
    confirmStatus: false,
    mutatingStatusValue: false,
    isConfirmationDialogOpen: false,
    confirmationDialogTitle: "",
    confirmationDialogMessage: "",
    confirmationDialogActionsAndHandlers: [],
    absence: null,
  });

  const setOpenDialog = (value: boolean) => setState((state) => ({ ...state, openDialog: value }));

  const handleCloseDialog = () => {
    setState((state) => ({
      ...state,
      error: "",
      openDialog: false,
    }));
    notifyParent();
  };

  return (
    <>
      <Grid
        sx={{
          display: "flex",
          alignItems: "center",
        }}
        onClick={() => setOpenDialog(true)}
      >
        {children}
      </Grid>
      {(hasAccess || allowedToViewOnly) && (
        <Dialog
          open={state.openDialog}
          onClose={handleCloseDialog}
          fullWidth
          maxWidth={"lg"}
        >
          <DialogTitle
            title={`${employee?.name}'s absences`}
            handleClose={handleCloseDialog}
          />
          <LoadingOverlay loading={state.loading}>
            <DialogContent>
              <PendingApprovals
                employee={employee}
                height={400}
              ></PendingApprovals>
            </DialogContent>
            <DialogActions
              handleCancel={handleCloseDialog}
              loading={state.loading}
              error={state.error}
            />
          </LoadingOverlay>
        </Dialog>
      )}
    </>
  );
};

export default memo(PendingApprovalModal);
